"use client"
/**
 * Render-only field widget components for CrudForm.
 * These components render individual field inputs and the top-level FieldControl dispatcher.
 * They have no state dependencies beyond their own local buffering.
 */
import * as React from 'react'
import dynamic from 'next/dynamic'
import remarkGfm from 'remark-gfm'
import type { MDEditorProps as UiWMDEditorProps } from '@uiw/react-md-editor'
import { Button } from '../../primitives/button'
import { TagsInput } from '../inputs/TagsInput'
import { ComboboxInput } from '../inputs/ComboboxInput'
import { DateTimePicker } from '../inputs/DateTimePicker'
import { TimePicker } from '../inputs/TimePicker'
import { DatePicker } from '../inputs/DatePicker'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { format, parseISO } from 'date-fns'
import { Info } from 'lucide-react'
import type { CrudField, CrudBuiltinField, CrudCustomField, CrudFieldOption } from './crudFormTypes'

export const EMPTY_OPTIONS: CrudFieldOption[] = []

// Local-buffer text input to avoid focus loss when parent re-renders
export function TextInput({
  value,
  onChange,
  placeholder,
  autoFocus,
  onSubmit,
  disabled,
  suggestions,
  inputType = 'text',
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  autoFocus?: boolean
  onSubmit?: () => void
  disabled?: boolean
  suggestions?: string[]
  inputType?: 'text' | 'password'
}) {
  const [local, setLocal] = React.useState<string>(value)
  const isFocusedRef = React.useRef(false)
  const userTypingRef = React.useRef(false)
  const datalistId = React.useId()

  React.useEffect(() => {
    if (!isFocusedRef.current || !userTypingRef.current) {
      setLocal(value)
    }
  }, [value])

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    const next = e.target.value
    userTypingRef.current = true
    setLocal(next)
    onChange(next)
  }, [disabled, onChange])

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onChange(local)
      onSubmit?.()
    }
  }, [disabled, local, onChange, onSubmit])

  const handleFocus = React.useCallback(() => { isFocusedRef.current = true }, [])

  const handleBlur = React.useCallback(() => {
    isFocusedRef.current = false
    userTypingRef.current = false
    onChange(local)
  }, [local, onChange])

  return (
    <>
      <input
        type={inputType}
        className="w-full h-9 rounded border px-2 text-sm"
        placeholder={placeholder}
        value={local}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        spellCheck={false}
        autoFocus={autoFocus}
        data-crud-focus-target=""
        disabled={disabled}
        list={suggestions && suggestions.length > 0 ? datalistId : undefined}
      />
      {suggestions && suggestions.length > 0 && (
        <datalist id={datalistId}>
          {suggestions.map((suggestion) => (
            <option key={suggestion} value={suggestion} />
          ))}
        </datalist>
      )}
    </>
  )
}

// Local-buffer number input to avoid focus loss when parent re-renders
export function NumberInput({
  value,
  onChange,
  placeholder,
  autoFocus,
  onSubmit,
}: {
  value: number | string | null | undefined
  onChange: (v: number | undefined) => void
  placeholder?: string
  autoFocus?: boolean
  onSubmit?: () => void
}) {
  const [local, setLocal] = React.useState<string>(value !== undefined && value !== null ? String(value) : '')
  const isFocusedRef = React.useRef(false)

  React.useEffect(() => {
    if (!isFocusedRef.current) {
      setLocal(value !== undefined && value !== null ? String(value) : '')
    }
  }, [value])

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value
    setLocal(next)
    const numValue = next === '' ? undefined : Number(next)
    onChange(numValue)
  }, [onChange])

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      const numValue = local === '' ? undefined : Number(local)
      onChange(numValue)
      onSubmit?.()
    }
  }, [local, onChange, onSubmit])

  const handleFocus = React.useCallback(() => { isFocusedRef.current = true }, [])

  const handleBlur = React.useCallback(() => {
    isFocusedRef.current = false
    const numValue = local === '' ? undefined : Number(local)
    onChange(numValue)
  }, [local, onChange])

  return (
    <input
      type="number"
      className="w-full h-9 rounded border px-2 text-sm"
      placeholder={placeholder}
      value={local}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      autoFocus={autoFocus}
      data-crud-focus-target=""
    />
  )
}

// Local-buffer textarea to avoid form-wide re-renders while typing
export function TextAreaInput({
  value,
  onChange,
  placeholder,
  autoFocus,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  autoFocus?: boolean
}) {
  const [local, setLocal] = React.useState<string>(value)
  const isFocusedRef = React.useRef(false)

  React.useEffect(() => {
    if (!isFocusedRef.current) setLocal(value)
  }, [value])

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value
    setLocal(next)
    onChange(next)
  }, [onChange])

  const handleFocus = React.useCallback(() => { isFocusedRef.current = true }, [])
  const handleBlur = React.useCallback(() => { isFocusedRef.current = false; onChange(local) }, [local, onChange])

  return (
    <textarea
      className="w-full rounded border px-2 py-2 min-h-[80px] sm:min-h-[120px] text-sm"
      placeholder={placeholder}
      value={local}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      autoFocus={autoFocus}
      data-crud-focus-target=""
    />
  )
}

type MDProps = { value?: string; onChange: (md: string) => void }
const MDEditor = dynamic(async () => {
  const mod = await import('@uiw/react-md-editor')
  return mod.default
}, { ssr: false }) as React.ComponentType<UiWMDEditorProps>

export const MarkdownEditor = React.memo(function MarkdownEditor({ value = '', onChange }: MDProps) {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const [local, setLocal] = React.useState<string>(value)
  const typingRef = React.useRef(false)

  React.useEffect(() => {
    if (!typingRef.current) setLocal(value)
  }, [value])

  const handleChange = React.useCallback((v?: string) => {
    typingRef.current = true
    setLocal(v ?? '')
  }, [])

  const commit = React.useCallback(() => {
    if (!typingRef.current) return
    typingRef.current = false
    onChange(local)
    requestAnimationFrame(() => {
      const ta = containerRef.current?.querySelector('textarea') as HTMLTextAreaElement | null
      ta?.focus()
    })
  }, [local, onChange])

  return (
    <div ref={containerRef} data-color-mode="light" className="w-full" onBlur={() => commit()}>
      <MDEditor
        value={local}
        height={220}
        onChange={handleChange}
        previewOptions={{ remarkPlugins: [remarkGfm] }}
      />
    </div>
  )
}, (prev, next) => prev.value === next.value)

type HtmlRTProps = { value?: string; onChange: (html: string) => void }
export const HtmlRichTextEditor = React.memo(function HtmlRichTextEditor({ value = '', onChange }: HtmlRTProps) {
  const t = useT()
  const boldLabel = t('ui.forms.richtext.bold')
  const italicLabel = t('ui.forms.richtext.italic')
  const underlineLabel = t('ui.forms.richtext.underline')
  const listLabel = t('ui.forms.richtext.list')
  const heading3Label = t('ui.forms.richtext.heading3')
  const linkLabel = t('ui.forms.richtext.link')
  const linkUrlPrompt = t('ui.forms.richtext.linkUrlPrompt')
  const ref = React.useRef<HTMLDivElement | null>(null)
  const applyingExternal = React.useRef(false)
  const typingRef = React.useRef(false)

  React.useEffect(() => {
    const el = ref.current
    if (!el) return
    const current = el.innerHTML
    if (!typingRef.current && current !== value) {
      applyingExternal.current = true
      el.innerHTML = value || ''
      requestAnimationFrame(() => { applyingExternal.current = false })
    }
  }, [value])

  const exec = (cmd: string, arg?: string) => {
    const el = ref.current
    if (!el) return
    el.focus()
    try {
      document.execCommand(cmd, false, arg)
    } catch {
      // ignore execCommand failures
    }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const isMod = e.metaKey || e.ctrlKey
    if (!isMod) return
    const k = e.key.toLowerCase()
    if (k === 'b') { e.preventDefault(); exec('bold') }
    if (k === 'i') { e.preventDefault(); exec('italic') }
    if (k === 'u') { e.preventDefault(); exec('underline') }
  }

  return (
    <div className="w-full rounded border">
      <div className="flex items-center gap-1 px-2 py-1 border-b">
        <Button variant="ghost" size="sm" className="h-auto px-2 py-0.5 text-xs" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')}>{boldLabel}</Button>
        <Button variant="ghost" size="sm" className="h-auto px-2 py-0.5 text-xs" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')}>{italicLabel}</Button>
        <Button variant="ghost" size="sm" className="h-auto px-2 py-0.5 text-xs" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('underline')}>{underlineLabel}</Button>
        <span className="mx-2 text-muted-foreground">|</span>
        <Button variant="ghost" size="sm" className="h-auto px-2 py-0.5 text-xs" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertUnorderedList')}>• {listLabel}</Button>
        <Button variant="ghost" size="sm" className="h-auto px-2 py-0.5 text-xs" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('formatBlock', '<h3>')}>{heading3Label}</Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-auto px-2 py-0.5 text-xs"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            const url = window.prompt(linkUrlPrompt)?.trim()
            if (url) exec('createLink', url)
          }}
        >{linkLabel}</Button>
      </div>
      <div
        ref={ref}
        className="w-full px-2 py-2 min-h-[100px] sm:min-h-[160px] focus:outline-none prose prose-sm max-w-none"
        contentEditable
        suppressContentEditableWarning
        onKeyDown={onKeyDown}
        onInput={() => { if (!applyingExternal.current) typingRef.current = true }}
        onBlur={() => {
          const el = ref.current
          if (!el) return
          typingRef.current = false
          onChange(el.innerHTML)
        }}
      />
    </div>
  )
}, (prev, next) => prev.value === next.value)

type SimpleMDProps = { value?: string; onChange: (md: string) => void }
export const SimpleMarkdownEditor = React.memo(function SimpleMarkdownEditor({ value = '', onChange }: SimpleMDProps) {
  const t = useT()
  const boldLabel = t('ui.forms.richtext.bold')
  const italicLabel = t('ui.forms.richtext.italic')
  const underlineLabel = t('ui.forms.richtext.underline')
  const markdownPlaceholder = t('ui.forms.richtext.placeholder')
  const sampleText = t('ui.forms.richtext.sampleText')
  const taRef = React.useRef<HTMLTextAreaElement | null>(null)
  const [local, setLocal] = React.useState<string>(value)
  const typingRef = React.useRef(false)

  React.useEffect(() => {
    if (!typingRef.current) setLocal(value)
  }, [value])

  const wrap = (before: string, after: string = before) => {
    const el = taRef.current
    if (!el) return
    const start = el.selectionStart ?? 0
    const end = el.selectionEnd ?? 0
    const sel = value.slice(start, end) || sampleText
    const next = value.slice(0, start) + before + sel + after + value.slice(end)
    onChange(next)
    queueMicrotask(() => {
      const caret = start + before.length + sel.length + after.length
      el.focus()
      el.setSelectionRange(caret, caret)
    })
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isMod = e.metaKey || e.ctrlKey
    if (!isMod) return
    const key = e.key.toLowerCase()
    if (key === 'b') { e.preventDefault(); wrap('**') }
    if (key === 'i') { e.preventDefault(); wrap('_') }
    if (key === 'u') { e.preventDefault(); wrap('__') }
  }

  return (
    <div className="w-full rounded border">
      <div className="flex items-center gap-1 px-2 py-1 border-b">
        <Button variant="ghost" size="sm" className="h-auto px-2 py-0.5 text-xs" onMouseDown={(e) => e.preventDefault()} onClick={() => wrap('**')}>{boldLabel}</Button>
        <Button variant="ghost" size="sm" className="h-auto px-2 py-0.5 text-xs" onMouseDown={(e) => e.preventDefault()} onClick={() => wrap('_')}>{italicLabel}</Button>
        <Button variant="ghost" size="sm" className="h-auto px-2 py-0.5 text-xs" onMouseDown={(e) => e.preventDefault()} onClick={() => wrap('__')}>{underlineLabel}</Button>
      </div>
      <textarea
        ref={taRef}
        className="w-full min-h-[100px] sm:min-h-[160px] resize-y px-2 py-2 font-mono text-sm outline-none"
        spellCheck={false}
        value={local}
        onChange={(e) => { typingRef.current = true; setLocal(e.target.value) }}
        onBlur={() => { if (typingRef.current) { typingRef.current = false; onChange(local) } }}
        onKeyDown={onKeyDown}
        placeholder={markdownPlaceholder}
      />
    </div>
  )
}, (prev, next) => prev.value === next.value)

export function RelationSelect({
  value,
  onChange,
  options,
  placeholder,
  autoFocus,
}: {
  value: string
  onChange: (v: string) => void
  options: CrudFieldOption[]
  placeholder?: string
  autoFocus?: boolean
}) {
  const t = useT()
  const [query, setQuery] = React.useState('')

  const filtered = React.useMemo(() => {
    const q = query.toLowerCase().trim()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q))
  }, [query, options])

  return (
    <div className="space-y-1">
      <input
        className="w-full h-9 rounded border px-2 text-sm"
        placeholder={placeholder || t('ui.forms.listbox.searchPlaceholder', 'Search...')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus={autoFocus}
        data-crud-focus-target=""
      />
      <div className="max-h-40 overflow-auto rounded border">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start rounded-none font-normal"
          onClick={() => onChange('')}
        >
          —
        </Button>
        {filtered.map((opt) => (
          <Button
            key={opt.value}
            variant="ghost"
            size="sm"
            className={`w-full justify-start rounded-none font-normal ${
              value === opt.value ? 'bg-muted' : ''
            }`}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </Button>
        ))}
      </div>
    </div>
  )
}

export const ListboxMultiSelect = React.memo(function ListboxMultiSelect({
  options,
  placeholder,
  value,
  onChange,
  autoFocus,
}: {
  options: CrudFieldOption[]
  placeholder?: string
  value: string[]
  onChange: (vals: string[]) => void
  autoFocus?: boolean
}) {
  const t = useT()
  const searchPlaceholder = placeholder || t('ui.forms.listbox.searchPlaceholder')
  const noMatchesLabel = t('ui.forms.listbox.noMatches')
  const [query, setQuery] = React.useState('')
  const filtered = React.useMemo(() => {
    const q = query.toLowerCase().trim()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q))
  }, [options, query])
  const toggle = React.useCallback(
    (val: string) => {
      const set = new Set(value)
      if (set.has(val)) set.delete(val)
      else set.add(val)
      onChange(Array.from(set))
    },
    [value, onChange]
  )
  return (
    <div className="w-full">
      <input
        className="mb-2 w-full h-8 rounded border px-2 text-sm"
        placeholder={searchPlaceholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus={autoFocus}
        data-crud-focus-target=""
      />
      <div className="rounded border max-h-48 overflow-auto divide-y">
        {filtered.map((opt) => {
          const isSel = value.includes(opt.value)
          return (
            <Button
              key={opt.value}
              variant="ghost"
              size="sm"
              onClick={() => toggle(opt.value)}
              className={`w-full justify-start rounded-none font-normal px-3 py-2 ${isSel ? 'bg-muted' : ''}`}
            >
              <span className="inline-flex items-center gap-2">
                <input type="checkbox" className="size-4" readOnly checked={isSel} />
                <span>{opt.label}</span>
              </span>
            </Button>
          )
        })}
        {!filtered.length ? (
          <div className="px-3 py-2 text-sm text-muted-foreground">{noMatchesLabel}</div>
        ) : null}
      </div>
    </div>
  )
})

export type FieldControlProps = {
  field: CrudField
  value: unknown
  error?: string
  options: CrudFieldOption[]
  setValue: (id: string, v: unknown) => void
  values: Record<string, unknown>
  loadFieldOptions: (field: CrudField, query?: string) => Promise<CrudFieldOption[]>
  autoFocus: boolean
  onSubmitRequest: () => void
  wrapperClassName?: string
  entityIdForField?: string
  recordId?: string
}

export const FieldControl = React.memo(function FieldControlImpl({
  field,
  value,
  error,
  options,
  setValue,
  values,
  loadFieldOptions,
  autoFocus,
  onSubmitRequest,
  wrapperClassName,
  entityIdForField,
  recordId,
}: FieldControlProps) {
  const t = useT()
  const fieldSetValue = React.useCallback(
    (nextValue: unknown) => setValue(field.id, nextValue),
    [setValue, field.id]
  )
  const setFormValue = React.useCallback(
    (targetId: string, nextValue: unknown) => setValue(targetId, nextValue),
    [setValue],
  )
  const builtin = field.type === 'custom' ? null : field as CrudBuiltinField
  const hasLoader = typeof builtin?.loadOptions === 'function'
  const disabled = Boolean(field.disabled)
  const readOnly = Boolean(field.readOnly)
  const autoFocusField = autoFocus && !disabled

  React.useEffect(() => {
    if (!hasLoader || field.type === 'custom') return
    loadFieldOptions(field).catch(() => {})
  }, [field, hasLoader, loadFieldOptions])

  const placeholder = builtin?.placeholder
  const rootClassName = wrapperClassName ? `space-y-1 ${wrapperClassName}` : 'space-y-1'

  return (
    <div className={rootClassName} data-crud-field-id={field.id}>
      {field.type !== 'checkbox' && field.label.trim().length > 0 ? (
        <label className="block text-sm font-medium">
          {field.label}
          {field.required ? <span className="text-red-600"> *</span> : null}
        </label>
      ) : null}
      {field.type === 'text' && (
        <TextInput
          value={value == null ? '' : String(value)}
          placeholder={placeholder}
          onChange={(next) => fieldSetValue(next)}
          autoFocus={autoFocusField}
          onSubmit={onSubmitRequest}
          disabled={disabled}
          suggestions={(field as CrudBuiltinField).suggestions}
        />
      )}
      {field.type === 'password' && (
        <TextInput
          value={value == null ? '' : String(value)}
          placeholder={placeholder}
          onChange={(next) => fieldSetValue(next)}
          autoFocus={autoFocusField}
          onSubmit={onSubmitRequest}
          disabled={disabled}
          inputType="password"
        />
      )}
      {field.type === 'number' && (
        <NumberInput
          value={typeof value === 'number' || typeof value === 'string' ? value : null}
          placeholder={placeholder}
          onChange={fieldSetValue}
          autoFocus={autoFocusField}
          onSubmit={onSubmitRequest}
        />
      )}
      {field.type === 'date' && (
        <input
          type="date"
          className="w-full h-9 rounded border px-2 text-sm"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => setValue(field.id, e.target.value || undefined)}
          autoFocus={autoFocusField}
          data-crud-focus-target=""
          disabled={disabled}
        />
      )}
      {field.type === 'datetime-local' && (
        <input
          type="datetime-local"
          className="w-full h-9 rounded border px-2 text-sm"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => setValue(field.id, e.target.value || undefined)}
          autoFocus={autoFocusField}
          data-crud-focus-target=""
          disabled={disabled}
        />
      )}
      {field.type === 'datepicker' && (
        <DatePicker
          value={typeof value === 'string' && value ? parseISO(value) : value instanceof Date ? value : null}
          onChange={(date) => setValue(field.id, date ? format(date, 'yyyy-MM-dd') : undefined)}
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          minDate={builtin?.minDate}
          maxDate={builtin?.maxDate}
          displayFormat={builtin?.displayFormat}
          closeOnSelect={builtin?.closeOnSelect}
          locale={builtin?.locale}
        />
      )}
      {field.type === 'datetime' && (
        <DateTimePicker
          value={typeof value === 'string' && value ? new Date(value) : value instanceof Date ? value : null}
          onChange={(date) => setValue(field.id, date ? date.toISOString() : undefined)}
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          minuteStep={builtin?.minuteStep}
          minDate={builtin?.minDate}
          maxDate={builtin?.maxDate}
          displayFormat={builtin?.displayFormat}
          locale={builtin?.locale}
        />
      )}
      {field.type === 'time' && (
        <TimePicker
          value={typeof value === 'string' ? value : null}
          onChange={(time) => setValue(field.id, time ?? undefined)}
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          minuteStep={builtin?.minuteStep}
        />
      )}
      {field.type === 'textarea' && (
        <TextAreaInput
          value={value == null ? '' : String(value)}
          placeholder={placeholder}
          onChange={(next) => fieldSetValue(next)}
          autoFocus={autoFocusField}
        />
      )}
      {field.type === 'richtext' && builtin?.editor === 'simple' && (
        <SimpleMarkdownEditor value={String(value ?? '')} onChange={fieldSetValue} />
      )}
      {field.type === 'richtext' && builtin?.editor === 'html' && (
        <HtmlRichTextEditor value={String(value ?? '')} onChange={fieldSetValue} />
      )}
      {field.type === 'richtext' && (!builtin?.editor || (builtin.editor !== 'simple' && builtin.editor !== 'html')) && (
        <MarkdownEditor value={String(value ?? '')} onChange={fieldSetValue} />
      )}
      {field.type === 'tags' && (
        <TagsInput
          value={Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []}
          onChange={(next) => fieldSetValue(next)}
          placeholder={placeholder}
          autoFocus={autoFocusField}
          suggestions={options.map((opt) => opt.label)}
          loadSuggestions={
            typeof builtin?.loadOptions === 'function'
              ? async (query?: string) => {
                  const opts = await loadFieldOptions(field, query)
                  return opts.map((opt) => opt.label)
                }
              : undefined
          }
        />
      )}
      {field.type === 'combobox' && (
        <ComboboxInput
          value={typeof value === 'string' ? value : String(value ?? '')}
          onChange={(next) => fieldSetValue(next)}
          placeholder={placeholder}
          autoFocus={autoFocusField}
          suggestions={
            builtin?.suggestions
              ? builtin.suggestions
              : options.map((opt) => ({ value: opt.value, label: opt.label }))
          }
          loadSuggestions={
            typeof builtin?.loadOptions === 'function'
              ? async (query?: string) => {
                  const opts = await loadFieldOptions(field, query)
                  return opts.map((opt) => ({ value: opt.value, label: opt.label }))
                }
              : undefined
          }
          allowCustomValues={builtin?.allowCustomValues ?? true}
          disabled={disabled}
        />
      )}
      {field.type === 'checkbox' && (
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            className="size-4"
            checked={value === true}
            onChange={(e) => setValue(field.id, e.target.checked)}
            data-crud-focus-target=""
            disabled={disabled}
          />
          <span className="text-sm">{field.label}</span>
        </label>
      )}
      {field.type === 'select' && !builtin?.multiple && (
        <select
          className="w-full h-9 rounded border pl-3 pr-8 text-sm"
          value={
            Array.isArray(value)
              ? String(value[0] ?? '')
              : value == null
                ? ''
                : String(value)
          }
          onChange={(e) => setValue(field.id, e.target.value || undefined)}
          data-crud-focus-target=""
          disabled={disabled}
        >
          <option value="">{t('ui.forms.select.emptyOption', '—')}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}
      {field.type === 'select' && builtin?.multiple && builtin.listbox === true && (
        <ListboxMultiSelect
          options={options}
          placeholder={placeholder}
          value={Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []}
          onChange={(vals) => setValue(field.id, vals)}
          autoFocus={autoFocusField}
        />
      )}
      {field.type === 'select' && builtin?.multiple && builtin.listbox !== true && (
        <div className="flex flex-wrap gap-3">
          {options.map((opt) => {
            const arr = Array.isArray(value)
              ? value.filter((item): item is string => typeof item === 'string')
              : []
            const checked = arr.includes(opt.value)
            return (
              <label key={opt.value} className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  className="size-4"
                  checked={checked}
                  onChange={(e) => {
                    const next = new Set(arr)
                    if (e.target.checked) {
                      next.add(opt.value)
                    } else {
                      next.delete(opt.value)
                    }
                    setValue(field.id, Array.from(next))
                  }}
                  disabled={disabled}
                />
                <span className="text-sm">{opt.label}</span>
              </label>
            )
          })}
        </div>
      )}
      {field.type === 'relation' && (
        <RelationSelect
          options={options}
          placeholder={placeholder}
          value={
            Array.isArray(value)
              ? String(value[0] ?? '')
              : value == null
                ? ''
                : String(value)
          }
          onChange={(selected) => setValue(field.id, selected)}
          autoFocus={autoFocusField}
        />
      )}
      {field.type === 'custom' && (
        <>
          {(field as CrudCustomField).component({
            id: field.id,
            value,
            error,
            setValue: fieldSetValue,
            setFormValue,
            values,
            entityId: entityIdForField,
            recordId,
            autoFocus,
            disabled,
          })}
        </>
      )}
      {field.description ? (
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>{field.description}</div>
        </div>
      ) : null}
      {error ? <div className="text-xs text-red-600">{error}</div> : null}
    </div>
  )
},
(prev, next) =>
  prev.field.id === next.field.id &&
  prev.field.type === next.field.type &&
  prev.field.label === next.field.label &&
  prev.field.description === next.field.description &&
  prev.field.required === next.field.required &&
  prev.value === next.value &&
  prev.error === next.error &&
  prev.options === next.options &&
  prev.loadFieldOptions === next.loadFieldOptions &&
  prev.autoFocus === next.autoFocus &&
  prev.onSubmitRequest === next.onSubmitRequest &&
  prev.wrapperClassName === next.wrapperClassName &&
  prev.entityIdForField === next.entityIdForField &&
  prev.recordId === next.recordId &&
  (prev.field.type !== 'custom' ||
    (prev.values === next.values &&
      prev.field.component === (next.field as CrudCustomField).component))
)
